// Dashboard model types - camelCase to match System.Text.Json serialization

export interface DashboardDietOrderChange {
  pKey_Diet_Flucs: number;
  fKey_Patient: number;
  change_Date: string;
  fullName: string;
  dietOrder: string;
  pendingDietText?: string;
  note?: string;
}

export interface DashboardTrayLineWarning {
  pKey_Patient: number;
  fullName: string;
  dietOrder: string;
}

export interface DashboardAdmittedResident {
  pKey_Patient: number;
  admitDate: string;
  admitDateOriginal: string;
  fullName: string;
  dietOrder: string;
  readmit: boolean;
}

export interface DashboardDischargedResident {
  pKey_Patient: number;
  dateTimeOfLog: string;
  fullName: string;
  isDeleted: boolean;
  floor?: string;
  wing?: string;
  unit?: string;
  bed_No?: string;
  room_No?: number;
  fKey_Log_Interaction_Type?: number;
}

export interface DashboardAllergyChange {
  fKey_Patient: number;
  dateTimeOfLog: string;
  fullName: string;
  allergyChanges: string;
}

export interface DashboardMealPatternNote {
  fKey_Patient: number;
  note_Date: string;
  completed_DateTime: string;
  note_Title: string;
  note_Text: string;
  fullName: string;
}

export interface DashboardSelectiveMenu {
  fKey_Patient: number;
  autoProcessedDateTime: string;
  fullName: string;
  changeType: number;
  transactionName: string;
  selectiveMenuItems?: DashboardSelectiveMenu[];
  menuDate: string;
  fKey_Menu_Header: number;
  menu_Name: string;
  guid: string;
  day: number;
  mealtime: number;
}

export interface DashboardCardConfig {
  cardId: string;
  order: number;
  isVisible: boolean;
  title: string;
  icon: string;
  gridColumn?: number;
  gridRow?: number;
  /** User-set pixel height for this card, saved with the layout.
   *  `undefined` means "auto" — the card sizes to its content, same
   *  as before. Only set via the drag handle rendered in edit mode. */
  height?: number;
}

export interface DashboardLayoutSettings {
  fKey_User: number;
  cards: DashboardCardConfig[];
  columnsCount: number;
  lastModified?: string;
  /**
   * Quick-action ids pinned to the dashboard, in order.
   *
   * Ids only — the definition of each action lives in `dashboardActions.ts`,
   * so moving a route does not require rewriting every user's saved row.
   *
   * `undefined` means never chosen (use the defaults); `[]` means chosen to
   * have none. Those are deliberately different, which is why this is
   * optional rather than defaulting to an empty array.
   *
   * Persisted inside the existing JSON blob in tblPMSec_User.Dashboard_Layout,
   * so it needed no schema change — see DashboardLayoutSettings.cs.
   */
  actions?: string[];
}

// Default card configuration - matches Blazor DashboardLayoutSettings.GetDefaultLayout()
export const DEFAULT_DASHBOARD_CARDS: DashboardCardConfig[] = [
  { cardId: 'DietOrderChanges', order: 0, isVisible: true, title: 'Diet Order Changes', icon: 'bi-clipboard2-pulse' },
  { cardId: 'TrayLineWarnings', order: 1, isVisible: true, title: 'Tray Line Warnings', icon: 'bi-exclamation-triangle' },
  { cardId: 'AdmittedResidents', order: 2, isVisible: true, title: 'Admitted Residents', icon: 'bi-person-plus' },
  { cardId: 'AdmittedGuests', order: 3, isVisible: true, title: 'Guests', icon: 'bi-people' },
  { cardId: 'DischargedResidents', order: 4, isVisible: true, title: 'Discharged Residents', icon: 'bi-person-dash' },
  { cardId: 'UnitRoomBedChanges', order: 5, isVisible: true, title: 'Unit/Room/Bed Changes', icon: 'bi-door-open' },
  { cardId: 'AllergyChanges', order: 6, isVisible: true, title: 'Allergy Changes', icon: 'bi-shield-exclamation' },
  { cardId: 'MealPatternNotes', order: 7, isVisible: true, title: 'Meal Pattern Notes', icon: 'bi-journal-text' },
  { cardId: 'SelectiveMenuAuto', order: 8, isVisible: true, title: 'Auto Generated Selective Menus', icon: 'bi-list-check' },
  { cardId: 'BedsideDevices', order: 9, isVisible: true, title: 'Bedside Devices', icon: 'bi-tv' },
];

export interface DashboardCensusSummary {
  totalResidents: number;
  totalGuests: number;
  dietTypeBreakdown: { name: string; count: number }[];
  unitBreakdown: { name: string; count: number }[];
}

export interface DashboardBirthday {
  pKey_Patient: number;
  fullName: string;
  birthDate: string;
  age: number;
  daysUntil: number;
  unit?: string;
  room_No?: number;
}

export interface DashboardWeightAlert {
  pKey_Patient: number;
  fullName: string;
  alertType: string;
  percentChange: number | null;
  daysSinceWeighed: number | null;
}

export interface DashboardMealServiceStatus {
  currentMealtime: string;
  /** Preformatted local-time string for the current mealtime
   *  (e.g. `"7:00 AM"`). Empty for placeholder states like
   *  "After Dinner". */
  currentMealtimeTime?: string;
  nextMealtime: string;
  /** Preformatted local-time string for the next mealtime. */
  nextMealtimeTime?: string;
  menuCycleName: string;
  menuCycleDay: number;
  trayTicketsPrinted: number;
  trayTicketsPending: number;
  selectiveMenusCompleted: number;
  selectiveMenusTotal: number;
  mealtimeBreakdown: { mealtime: string; count: number }[];
}

export const HOURS_OPTIONS = [
  { value: 12, label: '12 hours' },
  { value: 24, label: '24 hours' },
  { value: 48, label: '48 hours' },
  { value: 72, label: '3 days' },
  { value: 168, label: '7 days' },
] as const;
